import * as React from 'react';
import { useInput, useTranslate } from 'react-admin';
import { DateTimePicker } from '@material-ui/pickers';
import { makeStyles } from '@material-ui/core/styles';

interface MuiDateTimeInputProps {
    source: string;
    label?: string;
    helperText?: string;
    disabled?: boolean;
    fullWidth?: boolean;
    minDate?: Date | string;
    maxDate?: Date | string;
    [key: string]: any;
}

const useStyles = makeStyles({
    toolbar: {
        background: '#26A69A',
        padding: 16,
        color: 'white',
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
    },
    toolbarDate: {
        fontSize: 16,
        opacity: 0.85,
        lineHeight: 1.4,
    },
    toolbarTime: {
        fontSize: 32,
        fontWeight: 500,
        lineHeight: 1.2,
    },
});

const formatDate = (d: Date | null) =>
    d
        ? d.toLocaleDateString('fr-FR', {
              weekday: 'long',
              day: 'numeric',
              month: 'long',
          })
        : '—';
const formatTime = (d: Date | null) =>
    d
        ? d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
        : '--:--';

const MuiDateTimeInput: React.FC<MuiDateTimeInputProps> = props => {
    const { input, meta } = useInput(props as any);
    const translate = useTranslate();
    const classes = useStyles();
    const {
        label,
        helperText,
        disabled,
        fullWidth = true,
        minDate,
        maxDate,
    } = props;
    const value = input.value ? new Date(input.value) : null;
    const minD = minDate ? new Date(minDate) : undefined;
    const maxD = maxDate ? new Date(maxDate) : undefined;

    const ToolbarComponent = (toolbarProps: any) => {
        const d = toolbarProps.date ? new Date(toolbarProps.date) : null;
        return (
            <div className={classes.toolbar}>
                <div className={classes.toolbarDate}>{formatDate(d)}</div>
                <div className={classes.toolbarTime}>{formatTime(d)}</div>
            </div>
        );
    };

    return (
        <DateTimePicker
            inputVariant="filled"
            margin="normal"
            ampm={false}
            clearable
            {...(minD ? { minDate: minD, disablePast: true } : {})}
            {...(maxD ? { maxDate: maxD } : {})}
            disabled={disabled}
            fullWidth={fullWidth}
            label={label ? translate(label) : ''}
            format="dd/MM/yyyy HH:mm"
            value={value}
            onChange={date => {
                if (!date) return input.onChange(null);
                if (isNaN(date.getTime())) return;
                input.onChange(date.toISOString());
            }}
            error={!!(meta.touched && meta.error)}
            helperText={
                meta.touched && meta.error
                    ? translate(meta.error)
                    : helperText
                    ? translate(helperText)
                    : ''
            }
            okLabel="OK"
            cancelLabel={translate('ra.action.cancel')}
            clearLabel={translate('ra.action.clear_input_value')}
            ToolbarComponent={ToolbarComponent}
        />
    );
};

export default MuiDateTimeInput;
